/**
 * Calendar-month helpers (YYYY-MM keys, month option lists, month date spans).
 */

import {
  formatShortMonthShortYear,
  getDateInLocalTZFromDateStr,
  getDateString,
  getShiftedDate,
  today,
  toLocalCalendarDay,
} from "./core";

/**
 * Count distinct YYYY-MM-DD dates under an aggregate key and each YYYY-MM month.
 *
 * @param {Array<Object>|null|undefined} entries - Rows that include a date key.
 * @param {Object} [options]
 * @param {string} [options.allKey="all"] Bucket key for the full span.
 * @param {(entry: Object) => boolean} [options.includeEntry] Filter; default includes all.
 * @param {(entry: Object) => string|null|undefined} [options.getDateKey] Defaults to ``entry.date``.
 * @returns {Object<string, number>} Map of ``allKey`` / ``YYYY-MM`` → distinct day counts.
 */
export function countDistinctDaysByRangeKey(
  entries,
  {
    allKey = "all",
    includeEntry = () => true,
    getDateKey = (entry) => entry?.date,
  } = {}
) {
  const daysByKey = {
    [allKey]: new Set(),
  };

  (entries || []).forEach((entry) => {
    if (!includeEntry(entry)) return;
    const dateKey = getDateKey(entry);
    if (!dateKey) return;

    daysByKey[allKey].add(dateKey);
    const monthKey = String(dateKey).slice(0, 7);
    if (!daysByKey[monthKey]) {
      daysByKey[monthKey] = new Set();
    }
    daysByKey[monthKey].add(dateKey);
  });

  const counts = {};
  Object.keys(daysByKey).forEach((key) => {
    counts[key] = daysByKey[key].size;
  });
  return counts;
}

/**
 * Return the first and last local ISO dates for a YYYY-MM month key.
 *
 * @param {string} monthKey - Month value in YYYY-MM format.
 * @returns {{start: string, end: string}} Inclusive month date range.
 */
export function getMonthDateRange(monthKey) {
  const [year, month] = monthKey.split("-").map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return {
    start: getDateString(start),
    end: getDateString(end),
  };
}

/**
 * Inclusive end date for a period of N weeks / months / years starting on ``start``.
 *
 * Matches product examples: July 1 + 1 month → July 31 (start + unit − 1 day).
 *
 * @param {Date|string} start - Period start (Date or ``YYYY-MM-DD``).
 * @param {"week"|"month"|"year"} unit - Period unit.
 * @param {number} [count=1] - Number of units (e.g. 4 weeks, 6 months).
 * @returns {string} Inclusive end as ``YYYY-MM-DD``.
 */
export function getInclusivePeriodEndDateKey(start, unit, count = 1) {
  const amount = Math.max(1, Number(count) || 1);
  const startDay = toLocalCalendarDay(
    start instanceof Date ? start : getDateInLocalTZFromDateStr(String(start))
  );
  if (unit === "week") {
    return getDateString(getShiftedDate(startDay, amount * 7 - 1));
  }
  if (unit === "month") {
    const next = new Date(startDay);
    next.setMonth(next.getMonth() + amount);
    return getDateString(getShiftedDate(next, -1));
  }
  if (unit === "year") {
    const next = new Date(startDay);
    next.setFullYear(next.getFullYear() + amount);
    return getDateString(getShiftedDate(next, -1));
  }
  return getDateString(startDay);
}

/**
 * Build full-month suggestion chips from dates that appear in metric rows.
 *
 * Each chip covers the calendar month (1st → last day) and shows how many
 * distinct activity days exist in that month for the filtered entries.
 *
 * @param {Array<Object>|null|undefined} entries - Rows with a date field.
 * @param {Object} [options]
 * @param {(entry: Object) => boolean} [options.includeEntry] Filter; default all.
 * @param {(entry: Object) => string|null|undefined} [options.getDateKey] Defaults to ``entry.date``.
 * @returns {Array<{start: string, end: string, label: string, count: number, monthKey: string}>}
 */
export function buildFullMonthSuggestionsFromEntries(
  entries,
  {
    includeEntry = () => true,
    getDateKey = (entry) => entry?.date,
  } = {}
) {
  const dayCounts = countDistinctDaysByRangeKey(entries, {
    includeEntry,
    getDateKey,
  });

  return Object.keys(dayCounts)
    .filter((key) => key !== "all")
    .sort()
    .map((monthKey) => {
      const { start, end } = getMonthDateRange(monthKey);
      return {
        monthKey,
        start,
        end,
        label: formatShortMonthShortYear(start),
        count: dayCounts[monthKey] || 0,
      };
    });
}

/**
 * Build `{ value, label }` month options for every month from start through end
 * (inclusive). Accepts Date objects.
 *
 * @param {Date} startDate - Inclusive range start.
 * @param {Date} endDate - Inclusive range end.
 * @param {Object} [options]
 * @param {string} [options.locale="en-US"] Locale for month labels.
 * @returns {Array<{value: string, label: string}>}
 */
export function buildMonthOptionsBetweenDates(
  startDate,
  endDate,
  { locale = "en-US" } = {}
) {
  if (
    !(startDate instanceof Date) ||
    !(endDate instanceof Date) ||
    Number.isNaN(startDate.getTime()) ||
    Number.isNaN(endDate.getTime()) ||
    startDate > endDate
  ) {
    return [];
  }

  const monthFormatter = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  });

  const options = [];
  const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const last = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

  while (cursor <= last) {
    const value = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
    options.push({
      value,
      label: monthFormatter.format(cursor),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return options;
}

/**
 * Build recent month options ending at the current month (newest first).
 *
 * @param {number} [count=12] Number of months to include.
 * @returns {Array<{value: string, label: string, month: string, monthLabel: string, year: string}>}
 */
export function getRecentMonthOptions(count = 12) {
  const monthFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
  });
  const fullFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  });
  const now = today();

  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    return {
      value,
      label: fullFormatter.format(date),
      month: String(date.getMonth() + 1).padStart(2, "0"),
      monthLabel: monthFormatter.format(date),
      year: String(date.getFullYear()),
    };
  });
}
